import { z } from 'zod';
export const RUN_TITLE_MAX = 60;
const clean = (s) => s?.replace(/\s+/g, ' ').trim() ?? '';
/** Cap at a word boundary; "…" only when something was cut. */
export function capTitle(s, max = RUN_TITLE_MAX) {
    const t = clean(s);
    if (t.length <= max)
        return t;
    const cut = t.slice(0, max);
    const space = cut.lastIndexOf(' ');
    return `${(space > max * 0.5 ? cut.slice(0, space) : cut).replace(/[\s,;:.–—-]+$/, '')}…`;
}
const LEADING_VERBS = /^(?:please\s+)?(?:(?:can|could|would) you\s+)?(?:implement|build|create|add|make|do|work on|start|continue|finish|complete|fix|write|help me(?: with)?|let'?s|go ahead and|execute|run|handle|tackle)\s+(?:the\s+|a\s+|an\s+)?/i;
const MARKDOWN = /[`*_#>[\]]/g;
const ACRONYMS = new Set(['cli', 'ui', 'ux', 'api', 'mcp', 'e2e', 'ios', 'qr', 'ci', 'pr', 'sdk']);
const ACRONYM_CASE = { ios: 'iOS' };
/** `cli-ux-refresh.md` → "CLI UX refresh": file basename as words, known acronyms uppercase. */
export function humanizeFileName(name) {
    const base = (name.split(/[\\/]/).pop() ?? name).replace(/\.[A-Za-z0-9]{1,5}$/, '');
    const words = base.split(/[-_\s.]+/).filter(Boolean);
    return words
        .map((w, i) => {
        const lower = w.toLowerCase();
        if (ACRONYMS.has(lower))
            return ACRONYM_CASE[lower] ?? lower.toUpperCase();
        return i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w;
    })
        .join(' ');
}
const PATH_TOKEN = /(?:\.{0,2}\/)?(?:[\w.-]+\/)+[\w.-]+\.[A-Za-z0-9]{1,5}\b/g;
const BARE_PATH = /^(?:\.{0,2}\/)?(?:[\w.-]+\/)+[\w.-]+\.[A-Za-z0-9]{1,5}$/;
/** A title is never a bare path: a path-only title becomes its humanized file name. */
function unpath(title) {
    return BARE_PATH.test(title) ? humanizeFileName(title) : title;
}
/** Heuristic title from a prompt: its first clause, leading imperative verbs stripped. */
export function titleFromPrompt(prompt) {
    const line = ((prompt ?? '')
        .split('\n')
        .map((l) => l.replace(/^\s*(?:[-*+]|\d+[.)]|#+)\s*/, '').trim())
        .find((l) => l.length > 0) ?? '').replace(PATH_TOKEN, (m) => humanizeFileName(m));
    if (!line)
        return '';
    // First clause: split on " — ", " – ", ": ", "(", ". ", "; ".
    const clause = line.split(/\s[—–]\s|:\s|\s?\(|\.\s|;\s/)[0] ?? line;
    let t = clean(clause.replace(MARKDOWN, '')).replace(/[.:;,]+$/, '');
    const stripped = t.replace(LEADING_VERBS, '');
    if (stripped.length >= 3)
        t = stripped;
    if (!t)
        return '';
    return capTitle(t.charAt(0).toUpperCase() + t.slice(1));
}
/** A task file the prompt points at (`.claude/tasks/<name>.md`), as a repo-relative path. */
export function taskFileRef(prompt) {
    const m = /(?:^|[\s`'"(])((?:\.\/)?\.claude\/tasks\/[A-Za-z0-9._-]+\.md)\b/.exec(prompt ?? '');
    return m?.[1]?.replace(/^\.\//, '');
}
const STATUS_SUFFIX = /\s*[-–—:|]\s*(?:NOT[_ ]STARTED|IN[_ ]PROGRESS|DONE|BLOCKED|TODO|WIP|COMPLETE(?:D)?|READY|REVIEW)\s*$/i;
/** H1 of a markdown task file, without a trailing status (" - NOT_STARTED"). */
export function taskFileHeading(markdown) {
    const m = /^#\s+(.+?)\s*#*\s*$/m.exec(markdown ?? '');
    const t = clean(m?.[1]?.replace(STATUS_SUFFIX, '').replace(MARKDOWN, ''));
    return t ? capTitle(t, 120) : undefined;
}
export function deriveRunTitle(s) {
    const user = clean(s.userTitle);
    if (user)
        return { title: capTitle(user, 120), source: 'user' };
    const agent = unpath(clean(s.agentTitle));
    if (agent)
        return { title: capTitle(agent), source: 'agent' };
    const task = unpath(clean(s.taskTitle));
    if (task)
        return { title: capTitle(task, 120), source: 'task' };
    const prompt = titleFromPrompt(s.prompt);
    if (prompt)
        return { title: prompt, source: 'prompt' };
    return { title: '', source: 'none' };
}
/** Sealed `runtitle` entity value: the user's rename of a run. */
export const RunTitleEntry = z.strictObject({
    runId: z.string().min(1).max(160),
    title: z.string().max(200),
    updatedAt: z.number().int().nonnegative(),
});
/**
 * Entity id for a run's title: the run id itself when it is a v4 UUID, else a stable
 * UUID-shaped digest of it (the sealed value repeats the runId, and readers check it).
 */
export function runTitleEntityId(runId) {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(runId))
        return runId;
    const words = [0x811c9dc5, 0x01000193, 0x9e3779b9, 0x85ebca6b];
    for (let i = 0; i < runId.length; i++) {
        const c = runId.charCodeAt(i);
        for (let w = 0; w < 4; w++) {
            words[w] = Math.imul(words[w] ^ (c + w * 31), 0x01000193 + w * 2) >>> 0;
        }
    }
    const hex = words.map((w) => w.toString(16).padStart(8, '0')).join('');
    const variant = ((Number.parseInt(hex[16], 16) & 0x3) | 0x8).toString(16);
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}
//# sourceMappingURL=run-title.js.map